<template>
  <!--
    Like the notice board's must-read gate: a dialog with every dismissal path
    off. It steps aside on the profile page, where the student answers.
  -->
  <Dialog :visible="visible" modal :closable="false" :close-on-escape="false" :dismissable-mask="false" :draggable="false"
    :style="{ width: '36rem' }" :breakpoints="{ '640px': '95vw' }" :aria-label="`Your school needs details: ${current?.title ?? ''}`">
    <template #header>
      <div class="flex items-start gap-3" data-testid="data-request-gate">
        <i class="pi pi-id-card mt-0.5 text-xl text-primary" />
        <div class="min-w-0">
          <span class="text-xs font-medium text-surface-500">Before you continue</span>
          <h2 class="mt-0.5 text-lg font-semibold leading-tight">Your school needs some details from you</h2>
        </div>
      </div>
    </template>

    <div class="flex max-h-[50vh] flex-col gap-3 overflow-y-auto">
      <StudentDataRequestPreview v-for="request in blocking" :key="request.id" :title="request.title" :message="request.message"
        :dueLabel="dueText(request.due_on)" :fields="request.fields" />
    </div>

    <template #footer>
      <div class="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span class="text-xs text-surface-500">You can use the portal once these are filled in.</span>
        <Button label="Fill them in now" icon="pi pi-arrow-right" iconPos="right" data-testid="gate-fill" @click="goToForm" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import { useMyStudentDataRequests } from '@/composables/queries/useMyStudentDataRequests';
import StudentDataRequestPreview from '@/components/StudentDashboard/DataRequests/StudentDataRequestPreview.vue';
import { PROFILE_FORM_ROUTE, actionableRequests, blockingRequests, dueText, gateStepsAside } from '@/utils/studentDataRequests';

/**
 * Keeps the student portal behind a prompt while a request the school marked
 * "answer before using the portal" is unanswered. The list comes from the
 * server, which recomputes it after every save, so answering clears it.
 */
const route = useRoute();
const router = useRouter();
const { isCbtOnlyStudentPortal } = storeToRefs(useUserStore());
const { requests, refetch } = useMyStudentDataRequests();

const blocking = computed(() => blockingRequests(requests.value));
const current = computed(() => blocking.value[0] ?? null);
const visible = computed(() => blocking.value.length > 0 && !gateStepsAside(route, isCbtOnlyStudentPortal.value));

// A request closed (or a field closed) since the list loaded must stop
// blocking: check again as the student moves between pages, but only while
// something is waiting on them, and at most once every 30 seconds.
const RECHECK_MS = 30_000;
let lastCheck = Date.now();
watch(() => route.name, () => {
  if (!actionableRequests(requests.value).length || Date.now() - lastCheck < RECHECK_MS) return;
  lastCheck = Date.now();
  refetch()?.catch(() => {});
});

function goToForm() {
  router.push(PROFILE_FORM_ROUTE);
}
</script>
